/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  Grid,
  Box,
  Flex,
  Spacer,
  Tag,
  Center,
  CircularProgress,
  Checkbox,
  HStack,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Image,
  useToast,
  Link,
  useDisclosure,
} from '@chakra-ui/react';
import { MainTemplate } from '../../templates/MainTemplate';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input, SelectInput } from '../../components/atoms/Input';
import { useTheme } from '../../contexts/ThemeProvider';
import React, { useEffect } from 'react';
import { masks } from '@partiu/ui/masks';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Button } from '../../components/atoms/Button';
import { validarCNPJ, validarCPF } from '@partiu/ui/validateDocuments';
import { ApproveTermsModal } from './components/ApproveTermsModal';
import { SetPasswordModal } from './components/SetPasswordModal';
import { partiuClient } from '@partiu/api/axios';

export interface ICompanyForm {
  cnpj?: string;
  corporate_name?: string;
  name?: string;
  owner?: string;
  email?: string;
  description?: string;
  cellphone?: string;
  category?: string;
  cover_photo?: File;
  logo?: File;
  site?: string;
}

export interface IStore {
  postal_code?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  state?: string;
  city?: string;
  country?: string;
}

interface IRules {
  cumulativa?: boolean;
  delivery?: boolean;
  promocao?: boolean;
  disponibilidade?: boolean;
  outros?: boolean;
  outrosText?: string;
}

export interface IDiscount {
  discount?: string;
  title_discount?: string;
  link_desconto?: string;
  cupom_desconto?: string;
  rules?: IRules;
  rulesText?: string;
  mode?: string;
  type?: string;
  image?: File;
}

export interface IUser {
  full_name?: string;
  email?: string;
}

interface IForm {
  company: ICompanyForm;
  store: IStore;
  discount: IDiscount;
  user: IUser;
  accept_terms: boolean;
  approved: boolean;
  exclusive: boolean;
}

export function FormPartner() {
  const { colors, name } = useTheme();
  const [tabIndex, setTabIndex] = React.useState(0);

  const modalTerms = useDisclosure();
  const modalFinal = useDisclosure();

  const [location, setLocation] = React.useState<google.maps.LatLng | null>(null);
  const [map, setMap] = React.useState<google.maps.Map | null>(null);

  const toast = useToast();

  const formSchema = z.object({
    company: z.object({
      cnpj: z.string().min(1, 'CNPJ é obrigatório'),
      corporate_name: z
        .string()
        .min(1, 'Razão social é obrigatória')
        .max(140, 'Campo deve ter no máximo 140 caracteres'),
      name: z.string().min(1, 'Nome é obrigatório').max(140, 'Campo deve ter no máximo 140 caracteres'),
      owner: z.string().max(140, 'Campo deve ter no máximo 140 caracteres').optional(),
      email: z.string().optional(),
      cellphone: z.string().min(10, 'O celular deve ter pelo menos 10 caracteres'),
      category: z.string().min(1, 'Categoria é obrigatória'),
      description: z.string().min(1, 'Descrição é obrigatória').max(140, 'Campo deve ter no máximo 140 caracteres'),
      site: z.string().max(140, 'Campo deve ter no máximo 140 caracteres').optional(),
      cover_photo: z.any().refine((data) => data, {
        message: 'Capa é obrigatória',
        path: ['cover_photo'],
      }),
      logo: z.any().refine((data) => data, {
        message: 'Logo é obrigatório',
        path: ['logo'],
      }),
    }),
    store: z
      .object({
        postal_code: z.string(),
        street: z.string(),
        number: z.string(),
        neighborhood: z.string(),
        state: z.string(),
        city: z.string(),
        country: z.string(),
      })
      .optional(),
    discount: z.object({
      discount: z.string().optional(),
      type: z.string().min(1, 'Tipo de desconto é obrigatório'),
      mode: z.string().min(1, 'Modo é obrigatório'),
      rules: z
        .object({
          cumulativa: z.boolean().optional(),
          delivery: z.boolean().optional(),
          promocao: z.boolean().optional(),
          disponibilidade: z.boolean().optional(),
          outros: z.boolean().optional(),
          outrosText: z.string().optional(),
        })
        .optional(),
      image: z.any().refine((data) => data, {
        message: 'Imagem é obrigatória',
        path: ['image'],
      }),
      link_desconto: z.string().max(140, 'Campo deve ter no máximo 140 caracteres').optional(),
      cupom_desconto: z.string().max(140, 'Campo deve ter no máximo 140 caracteres').optional(),
      title_discount: z
        .string()
        .min(1, 'Título do desconto é obrigatório')
        .max(140, 'Campo deve ter no máximo 140 caracteres'),
    }),
    user: z.object({
      full_name: z.string().min(1, 'Nome completo é obrigatório').max(140, 'Campo deve ter no máximo 140 caracteres'),
      email: z.string().email('Endereço de email inválido'),
    }),
    accept_terms: z.boolean(),
    approved: z.boolean(),
  });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyB5tXrkiapXmzmvkkW0-7MQvfx1LL9VScQ',
  });

  const onLoad = function callback(map: any) {
    setMap(map);
  };

  const methods = useForm<IForm>({
    resolver: zodResolver(formSchema),
  });

  const {
    control,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const discoutMode = watch('discount.mode');

  const values = watch();

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  const onSubmit = (data: IForm) => {
    if (data.accept_terms === false || data.approved === false) {
      toast({
        title: 'Oops!',
        description: 'Aceite os termos para continuar',
        status: 'error',
      });
      return;
    }
    let hasError = false;

    if (data.discount.mode === 'online' && !data.company.site) {
      hasError = true;
      setError('company.site', {
        message: 'Site é obrigatório',
      });
    }

    if (data.discount.mode === 'online' && !data.discount.link_desconto) {
      hasError = true;
      setError('discount.link_desconto', {
        message: 'Link de desconto é obrigatório',
      });
    }

    if (data.discount.mode === 'online' && !data.discount.cupom_desconto) {
      hasError = true;
      setError('discount.cupom_desconto', {
        message: 'Cupom de desconto é obrigatório',
      });
    }

    data.company.name = data.user.full_name;
    data.company.email = data.user.email;

    if (
      data.discount.type === 'discount' &&
      (parseInt(data?.discount?.discount || '0') < 1 || parseInt(data?.discount?.discount || '0') > 100)
    ) {
      hasError = true;
      setError('discount.discount', {
        message: 'O desconto deve ser entre 1 e 100%',
      });
    }

    if (!validarCPF(data.company.cnpj) && !validarCNPJ(data.company.cnpj)) {
      hasError = true;
      setError('company.cnpj', {
        message: 'CNPJ ou inválido',
      });
    }

    if (hasError) {
      toast({
        title: 'Oops!',
        description: 'Verifique os campos obrigatórios',
        status: 'error',
      });
      return;
    }

    modalTerms.onOpen();
  };

  const handleGetCep = async (cep: string) => {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();

    setValue('store.street', data.logradouro);
    setValue('store.neighborhood', data.bairro);
    setValue('store.state', data.uf);
    setValue('store.city', data.localidade);
    setValue('store.country', 'Brasil');

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: `${data.logradouro}, ${data.localidade} - ${data.uf}` }, function (results, status) {
      if (status == window.google.maps.GeocoderStatus.OK && results) {
        if (map) {
          map.setCenter(results[0].geometry.location);
        }
        setLocation(results[0].geometry.location);
      }
    });
    setLocation(data);
  };

  useEffect(() => {
    let text = '';
    if (values?.discount?.rules?.promocao) {
      text += '<p>Promoção válida somente para essa unidade.</p>';
    }
    if (values?.discount?.rules?.delivery) {
      text += '<p>Exceto Delivery.</p>';
    }
    if (values?.discount?.rules?.cumulativa) {
      text += '<p>Não cumulativa com outras promoções.</p>';
    }
    if (values?.discount?.rules?.disponibilidade) {
      text += '<p>Sujeito a disponibilidade do produto e lotação do estabelecimento.</p>';
    }
    if (values?.discount?.rules?.outros) {
      text += `<p>${values?.discount?.rules?.outrosText}</p>`;
    }
    setValue('discount.rulesText', text);
  }, [
    values?.discount?.rules?.promocao,
    values?.discount?.rules?.delivery,
    values?.discount?.rules?.cumulativa,
    values?.discount?.rules?.disponibilidade,
    values?.discount?.rules?.outros,
    values?.discount?.rules?.outrosText,
  ]);

  useEffect(() => {
    const cep = masks.unmask(values?.store?.postal_code || '');
    if (cep.length === 8) {
      handleGetCep(cep);
    }
  }, [values?.store?.postal_code]);

  useEffect(() => {
    clearErrors();
    if (values?.company?.cnpj === null || masks.unmask(values?.company?.cnpj || '').length < 14) return;
    try {
      if (validarCNPJ(values?.company?.cnpj)) {
        partiuClient.api.getCompanyDataFromCNPJ(masks.unmask(values?.company?.cnpj || '')).then((data: any) => {
          setValue('company.name', data.fantasia || '');
          setValue('company.owner', data?.qsa?.[0]?.nome || '');
          setValue('company.cellphone', data.telefone || '');
          setValue('company.corporate_name', data.nome || '');
          setValue('company.email', data.email || '');
          setValue('user.email', data.email || '');
          setValue('user.full_name', data?.qsa?.[0]?.nome || '');
          setValue('store.postal_code', data.cep ? masks.zipcode(data.cep) : '');
          setValue('store.street', data.logradouro || '');
          setValue('store.number', data.numero || '');
          setValue('store.neighborhood', data.bairro || '');
          setValue('store.state', data.uf || '');
          setValue('store.city', data.municipio || '');
          setValue('store.country', 'Brasil');
        });
      } else {
        setError('company.cnpj', {
          message: 'CNPJ inválido',
        });
      }
    } catch (e) {
      console.log(e);
    }
  }, [values?.company?.cnpj]);

  return (
    <MainTemplate>
      <VStack width={'100%'} padding="0px 20px" paddingTop={'20px'}>
        <Heading textAlign={'center'}>Vamos cadastrar seu estabelecimento?</Heading>
        <Grid
          marginTop={'20px'}
          width={'100%'}
          templateColumns={{
            base: '1fr',
            md: '1fr 1fr',
          }}
          gap={6}
        >
          <Box width={'100%'} position={'relative'}>
            <Card
              position={{
                base: 'relative',
                md: 'fixed',
              }}
              width="100%"
              maxW={{
                base: '100%',
                md: '400px',
              }}
            >
              <CardHeader paddingBottom="0px">
                <Heading color={colors.secondaryColor} fontSize={'15pt'}>
                  {values?.company?.name || 'Nome da Empresa'}
                </Heading>
                <Flex gap={2} marginTop="10px" alignItems="flex-start" w="100%" pb="10px">
                  <Tag background={colors.primaryColor} color="white">
                    {values?.company?.category || 'Categoria'}
                  </Tag>
                  <Tag background={colors.primaryColor} color="white">
                    {values?.discount?.mode === 'local' ? 'Parceiro Local' : 'Parceiro E-Commerce'}
                  </Tag>
                </Flex>
              </CardHeader>
              <CardBody padding="0px 0px" pb="20px">
                <VStack w="100%">
                  <Flex w="100%" alignItems="flex-start">
                    <VStack spacing={2} fontSize="15px" alignItems="flex-start">
                      <Tabs index={tabIndex} onChange={handleTabsChange} w="100%">
                        <TabList>
                          <Tab
                            fontSize="16px"
                            fontWeight="500"
                            _selected={{
                              fontWeight: '600',
                              color: colors.primaryColor,
                              borderBottom: `2px solid ${colors.primaryColor}`,
                            }}
                          >
                            Vantagens
                          </Tab>
                          <Tab
                            fontSize="16px"
                            fontWeight="400"
                            _selected={{
                              fontWeight: '600',
                              color: colors.primaryColor,
                              borderBottom: `2px solid ${colors.primaryColor}`,
                            }}
                          >
                            Sobre
                          </Tab>
                          <Tab
                            fontSize="16px"
                            fontWeight="400"
                            _selected={{
                              fontWeight: '600',
                              color: colors.primaryColor,
                              borderBottom: `2px solid ${colors.primaryColor}`,
                            }}
                          >
                            Regulamento
                          </Tab>
                        </TabList>
                        <TabPanels>
                          <TabPanel>
                            <VStack paddingBottom="10px" alignItems="flex-start" spacing={0}>
                              <Text textAlign="left" color="#777" fontSize="12px">
                                Aperte no desconto para mais informações
                              </Text>
                            </VStack>
                            <Stack alignItems="flex-start" w="100%" spacing={5}>
                              <VStack borderRadius="md" p="10px" border="1.5px dashed #E5E5E5" w="100%">
                                <HStack width="100%">
                                  {values?.discount?.image ? (
                                    <>
                                      <Image
                                        marginRight="20px"
                                        width={{ base: '150px' }}
                                        height={{ base: '100px' }}
                                        borderRadius="10px"
                                        objectFit="cover"
                                        src={URL.createObjectURL(values?.discount?.image)}
                                      />
                                    </>
                                  ) : (
                                    <Box
                                      marginRight="20px"
                                      backgroundColor={'#AAA'}
                                      width={{ base: '150px' }}
                                      height={{ base: '100px' }}
                                      borderRadius="10px"
                                      objectFit="cover"
                                    />
                                  )}
                                  <VStack
                                    bgColor="white"
                                    transition="0.2s"
                                    cursor="pointer"
                                    alignItems="flex-start"
                                    width="100%"
                                    spacing={0}
                                  >
                                    <HStack alignItems="center" spacing={3}>
                                      <i className="fas fa-tag" style={{ color: colors.primaryColor }} />
                                      <Text fontSize="8pt" textAlign="left" color={colors.primaryColor}>
                                        {values?.discount?.discount || '10'}% de desconto
                                      </Text>
                                    </HStack>
                                    <Text textAlign="left" pt="5px" fontSize="12px">
                                      <b>{values?.discount?.title_discount ? values?.discount?.title_discount : ' '}</b>
                                      <Text>Descrição da promoção</Text>
                                    </Text>
                                  </VStack>
                                </HStack>
                              </VStack>
                            </Stack>
                          </TabPanel>

                          <TabPanel>
                            <VStack paddingTop="10px" textAlign="left" alignItems="flex-start" w="100%">
                              <Text style={{ lineBreak: 'anywhere' }}>{values?.company?.description}</Text>
                              {values?.company?.site !== '' ? (
                                <HStack wordBreak="break-all">
                                  <i className="fas fa-at" />
                                  <Text>{values?.company?.site}</Text>
                                </HStack>
                              ) : (
                                <></>
                              )}
                            </VStack>
                          </TabPanel>
                          <TabPanel>
                            <VStack
                              maxH={'200px'}
                              wordBreak={'break-word'}
                              overflowX={'hidden'}
                              overflowY={'auto'}
                              textAlign="left"
                              padding="5px"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <Text fontSize="18px">{values?.company?.corporate_name}</Text>
                              <Text fontSize="18px">{values?.discount?.discount || '10'}% DE DESCONTO</Text>

                              <Text fontSize="18px">REGULAMENTO</Text>
                              {typeof values?.discount?.rulesText === 'string' && (
                                <Text fontSize="14px">
                                  <div dangerouslySetInnerHTML={{ __html: values?.discount?.rulesText }} />
                                </Text>
                              )}
                            </VStack>
                          </TabPanel>
                        </TabPanels>
                      </Tabs>
                    </VStack>
                    <Spacer />
                  </Flex>
                </VStack>
              </CardBody>
            </Card>
          </Box>
          <Box width={'100%'}>
            <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={5} align={'flex-start'} width={'100%'}>
                <Card width={'100%'}>
                  <CardHeader>
                    <Heading fontSize={'21px'}>Dados da Empresa</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={5} align={'flex-start'} width={'100%'}>
                      <Input control={control} name="company.cnpj" label="CNPJ/CPF *" mask="document" />
                      <Input control={control} name="company.corporate_name" label="Razão Social *" />
                      <Input control={control} name="company.name" label="Nome Fantasia *" />
                      <SelectInput
                        options={[
                          { value: 'Cinema', label: 'Cinema' },
                          { value: 'Vestuário', label: 'Vestuário' },
                          { value: 'Beleza', label: 'Beleza' },
                          { value: 'Turismo', label: 'Turismo' },
                          { value: 'Educação', label: 'Educação' },
                          { value: 'Para Casa', label: 'Para Casa' },
                          { value: 'Farmácia', label: 'Farmácia' },
                          { value: 'Alimentação', label: 'Alimentação' },
                          { value: 'Saúde', label: 'Saúde' },
                          { value: 'Serviços', label: 'Serviços' },
                          { value: 'Petshop', label: 'Petshop' },
                          { value: 'Para o meu negócio', label: 'Para o meu negócio' },
                          { value: 'Automotivo', label: 'Automotivo' },
                          { value: 'Delivery', label: 'Delivery' },
                          { value: 'Diversão', label: 'Diversão' },
                          { value: 'Outros', label: 'Outros' },
                        ]}
                        placeholder="Escolha uma categoria"
                        control={control}
                        name="company.category"
                        label="Categoria *"
                        autoWidth={false}
                        multiple={false}
                        native={false}
                      />

                      <SelectInput
                        options={[
                          { value: 'local', label: 'Loja Física' },
                          { value: 'online', label: 'E-commerce' },
                        ]}
                        placeholder="Escolha uma categoria"
                        control={control}
                        name="discount.mode"
                        label="Categoria *"
                        autoWidth={false}
                        multiple={false}
                        native={false}
                      />
                      <Input control={control} name="company.description" label="Descrição da Loja" />
                      <Input control={control} name="company.site" label="Site da Loja" />
                    </VStack>
                  </CardBody>
                </Card>

                {discoutMode === 'local' && (
                  <Card width={'100%'}>
                    <CardHeader>
                      <Heading fontSize={'21px'}>Dados da Loja</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={5} align={'flex-start'} width={'100%'}>
                        <Input control={control} mask="zipcode" name="store.postal_code" label="CEP *" />
                        <Input control={control} name="store.street" label="Rua *" />
                        <Input control={control} name="store.complement" label="Complemento" />
                        <Input control={control} mask="number" name="store.number" label="Número *" />
                        <Input control={control} name="store.neighborhood" label="Bairro *" />
                        <Input control={control} name="store.state" label="Estado *" />
                        <Input control={control} name="store.city" label="Cidade *" />
                        <Input control={control} name="store.country" label="País *" />
                        {isLoaded ? (
                          <GoogleMap
                            mapContainerStyle={{
                              width: '100%',
                              height: '300px',
                              borderRadius: '10px',
                            }}
                            center={location || undefined}
                            zoom={17}
                            onLoad={onLoad}
                          >
                            {location && (
                              <Marker position={location} draggable={true} onDragEnd={(e) => setLocation(e.latLng)} />
                            )}
                          </GoogleMap>
                        ) : (
                          <Center>
                            <CircularProgress isIndeterminate />
                          </Center>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                )}

                <Card width={'100%'}>
                  <CardHeader>
                    <Heading fontSize={'21px'}>Dados para contato</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={5} align={'flex-start'} width={'100%'}>
                      <Input control={control} name="user.full_name" label="Nome do Dono *" />
                      <Input control={control} name="user.email" label="Email do Dono *" />
                      <Input control={control} mask="cell" name="company.cellphone" label="Telefone do Dono *" />
                    </VStack>
                  </CardBody>
                </Card>

                <Card width={'100%'}>
                  <CardHeader>
                    <Heading fontSize={'21px'}>Dados do Desconto</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={5} align={'flex-start'} width={'100%'}>
                      <SelectInput
                        options={[
                          { value: 'discount', label: 'Porcentagem' },
                          { value: 'buy_and_gain', label: 'Compre e Ganhe' },
                          { value: 'treat', label: 'Mimo' },
                        ]}
                        placeholder="Selecione o tipo do desconto"
                        control={control}
                        name="discount.type"
                        label="Tipo de Desconto *"
                        autoWidth={false}
                        multiple={false}
                        native={false}
                      />
                      {values?.discount?.type === 'discount' && (
                        <Input
                          type="number"
                          control={control}
                          maxLength={4}
                          mask="number"
                          name="discount.discount"
                          label="Valor do Desconto*"
                        />
                      )}
                      <Input name="discount.title_discount" control={control} label="Título do Desconto *" />
                      <Heading textAlign="left" marginBottom="10px" fontSize="14px">
                        REGULAMENTO
                      </Heading>

                      <Checkbox
                        isChecked={typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.cumulativa}
                        onChange={(e) => setValue('discount.rules.cumulativa', e.target.checked)}
                      >
                        Não cumulativa com outras promoções.
                      </Checkbox>
                      <Checkbox
                        isChecked={typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.delivery}
                        onChange={(e) => setValue('discount.rules.delivery', e.target.checked)}
                      >
                        Exceto Delivery.
                      </Checkbox>
                      <Checkbox
                        isChecked={typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.promocao}
                        onChange={(e) => setValue('discount.rules.promocao', e.target.checked)}
                      >
                        Promoção válida somente para essa unidade.
                      </Checkbox>
                      <Checkbox
                        isChecked={
                          typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.disponibilidade
                        }
                        onChange={(e) => setValue('discount.rules.disponibilidade', e.target.checked)}
                      >
                        Sujeito a disponibilidade do produto e lotação do estabelecimento.
                      </Checkbox>
                      <Checkbox
                        isChecked={typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.outros}
                        onChange={(e) => setValue('discount.rules.outros', e.target.checked)}
                      >
                        Outros
                      </Checkbox>
                      {typeof values?.discount?.rules !== 'string' && values?.discount?.rules?.outros && (
                        <Input
                          control={control}
                          name="discount.rules.outrosText"
                          label="Digite o regulamento complementar"
                        />
                      )}

                      {discoutMode === 'online' && (
                        <Input control={control} name="discount.link_desconto" label="Link de desconto*" />
                      )}

                      {discoutMode === 'online' && (
                        <Input control={control} name="discount.cupom_desconto" label="Cupom de Desconto*" />
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card width={'100%'}>
                  <CardHeader>
                    <Heading fontSize={'21px'}>Imagens</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={5} align={'flex-start'} width={'100%'}>
                      <Input
                        control={control}
                        accept="image/*"
                        name="company.cover_photo"
                        label="Capa* (1080px x 764px)"
                        type="file"
                      />
                      <Input
                        control={control}
                        accept="image/*"
                        name="discount.image"
                        label="Promoção* (150px x 150px)"
                        type="file"
                      />
                      <Input
                        control={control}
                        accept="image/*"
                        name="company.logo"
                        label="Logo* (250px x 160px)"
                        type="file"
                      />
                    </VStack>
                  </CardBody>
                </Card>

                <Card width={'100%'} marginBottom={'100px'}>
                  <CardHeader>
                    <Heading fontSize={'21px'}>Aceites</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={5} align={'flex-start'} width={'100%'}>
                      <Checkbox
                        isChecked={values?.accept_terms}
                        onChange={(e) => setValue('accept_terms', e.target.checked)}
                      >
                        Aceito os{' '}
                        <Link color={colors.primaryColor} target="_blank" href="/legal/be-partner/termos-de-uso">
                          Termos de Uso
                        </Link>
                      </Checkbox>
                      {errors?.accept_terms && (
                        <Text color={'red.500'} fontSize={'12px'}>
                          {errors?.accept_terms?.message}
                        </Text>
                      )}
                      <Checkbox isChecked={values?.exclusive} onChange={(e) => setValue('exclusive', e.target.checked)}>
                        Quero ser um parceiro exclusivo {name}
                      </Checkbox>
                      {errors?.accept_terms && (
                        <Text color={'red.500'} fontSize={'12px'}>
                          {errors?.accept_terms?.message}
                        </Text>
                      )}
                      <Checkbox isChecked={values?.approved} onChange={(e) => setValue('approved', e.target.checked)}>
                        Entendo que o desconto só será disponibilizado quando aprovado pela {name}!
                      </Checkbox>
                      {errors?.accept_terms && (
                        <Text color={'red.500'} fontSize={'12px'}>
                          {errors?.accept_terms?.message}
                        </Text>
                      )}
                      <Button
                        type="submit"
                        background={colors.primaryColor}
                        borderRadius={'100px'}
                        size={'md'}
                        color="white"
                        variant={'solid'}
                        width={'fit-content'}
                        padding="0px 40px"
                        marginLeft={'auto'}
                      >
                        Enviar
                      </Button>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </form>
          </Box>
        </Grid>
      </VStack>
      <ApproveTermsModal
        onAccept={() => {
          modalTerms.onClose();
          modalFinal.onOpen();
        }}
        company={values.company}
        store={values.store}
        discount={values.discount}
        isOpen={modalTerms.isOpen}
        onClose={modalTerms.onClose}
      />

      <SetPasswordModal
        discount={values.discount}
        exclusive={values.exclusive}
        store={values.store}
        user={values.user}
        company={values.company}
        isOpen={modalFinal.isOpen}
        onClose={modalFinal.onClose}
        onAccept={() => {
          reset();
          modalFinal.onClose();
        }}
      />
    </MainTemplate>
  );
}
